/**
 * <jc-card-buy>: product-card quantity + Add to Cart.
 * Mirrors Horizon's product-form flow: announces a CartLinesUpdateEvent, posts
 * to /cart/add with the cart sections to re-render, then resolves the event
 * with the refreshed cart so the cart drawer, bubble and totals update.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const ADDED_RESET_MS = 2000;

/** Horizon exposes routes/translations on a global `Theme` const (not on window). */
// eslint-disable-next-line no-undef
const theme = typeof Theme !== 'undefined' ? Theme : {};

class JcCardBuy extends HTMLElement {
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
  }

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (this.form);
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    const label = button?.querySelector('[data-label]');
    const status = form.querySelector('[data-status]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    button.setAttribute('aria-busy', 'true');
    const originalLabel = label?.innerHTML ?? '';

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(theme.routes?.cart_add_url ?? '/cart/add.js', {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      const cart = await fetch(`${theme.routes?.cart_url ?? '/cart'}.json`, {
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());

      const failed = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'jc-card-buy',
          sourceId: this.dataset.productId,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });

      if (failed) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID', detail: { description: response.description } }));
        if (status) status.textContent = response.description || response.message || 'Could not add to cart';
        if (label) label.textContent = response.message || 'Unavailable';
      } else {
        const added = theme.translations?.added || 'Added';
        if (status) status.textContent = added;
        if (label) label.textContent = added;
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      if (status) status.textContent = 'Could not add to cart';
    } finally {
      button.removeAttribute('aria-busy');
      window.setTimeout(() => {
        if (label) label.innerHTML = originalLabel;
        if (status) status.textContent = '';
      }, ADDED_RESET_MS);
    }
  }
}

if (!customElements.get('jc-card-buy')) {
  customElements.define('jc-card-buy', JcCardBuy);
}
